import { FC, MouseEvent, useState } from 'react';
import { LoaderCircle, Pause, Play, Trash2 } from 'lucide-react';
import { Can } from '@new/casl';
import {
  useStartCourseMutation,
  usePauseCourseMutation,
  useResumeCourseMutation,
  useGetCurrentUserQuery,
  useRemoveCourseMutation,
  teachurKeys,
} from '@teachur/http-client';
import { Button } from '@new/common/ui';
import { ConfirmationModal } from '@new/common/ui/ConfirmationModal/ConfirmationModal';
import { getErrorMessage } from '@new/lib';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

type CourseStatus = 'notStarted' | 'inProgress' | 'completed';

interface CourseActionButtonProps {
  courseId: number;
  status: CourseStatus;
  isPaused: boolean;
  degreeId?: number;
  readOnly?: boolean;
}

export const CourseActionButton: FC<CourseActionButtonProps> = ({
  courseId,
  status,
  isPaused,
  readOnly,
}) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const queryClient = useQueryClient();
  const { data: currentUser } = useGetCurrentUserQuery();

  const { mutate: startCourse, isLoading: isStarting } = useStartCourseMutation({
    onError: (error: unknown) => toast.error(getErrorMessage(error, 'Failed to start course')),
  });

  const { mutate: pauseCourse, isLoading: isPausing } = usePauseCourseMutation({
    onError: (error: unknown) => toast.error(getErrorMessage(error, 'Failed to pause course')),
  });

  const { mutate: resumeCourse, isLoading: isResuming } = useResumeCourseMutation({
    onError: (error: unknown) => toast.error(getErrorMessage(error, 'Failed to resume course')),
  });

  const isActionPending = isStarting || isPausing || isResuming;

  const { mutate: removeCourse, isPending: isRemoving } = useRemoveCourseMutation({
    onSuccess: () => {
      toast.success('Course removed successfully');

      queryClient.invalidateQueries({
        queryKey: teachurKeys.courses.list(),
      });

      queryClient.invalidateQueries({
        queryKey: teachurKeys.courses.available(),
      });

      setIsDeleteModalOpen(false);
    },
    onError: (error: unknown) => toast.error(getErrorMessage(error, 'Failed to remove course')),
  });

  const onClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (status === 'inProgress' && !isPaused) {
      pauseCourse(courseId);
    } else if (isPaused) {
      resumeCourse(courseId);
    } else {
      startCourse(courseId);
    }
  };

  const onOpenDeleteModal = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDeleteModalOpen(true);
  };

  const onCloseDeleteModal = () => {
    if (isRemoving) return;

    setIsDeleteModalOpen(false);
  };

  const onConfirmDelete = () => {
    if (currentUser?.id) {
      removeCourse({
        courseId,
        userId: currentUser.id,
      });
    }
  };

  if (status === 'completed') {
    return null;
  }

  const icon = isPaused ? <Play size={16} /> : status !== 'notStarted' ? <Pause size={16} /> : null;

  let buttonText = 'Start';

  if (isPaused) {
    buttonText = 'Resume';
  } else if (status !== 'notStarted') {
    buttonText = 'Pause';
  }

  return (
    <Can I="start" a="Course" context={{ readOnly }}>
      <div className="flex w-full md:w-fit items-center justify-between md:self-center gap-2">
        <Button
          variant="primary"
          onClick={onClick}
          disabled={isActionPending}
          className="w-full flex-1"
          text={buttonText}
          leftIcon={
            isActionPending ? (
              <LoaderCircle
                size={16}
                className="animate-spin"
                role="status"
                aria-label="Course action in progress"
              />
            ) : (
              icon
            )
          }
          aria-label={isActionPending ? `${buttonText} in progress` : buttonText}
        />

        <Button
          variant="ghost"
          onClick={onOpenDeleteModal}
          disabled={isRemoving}
          aria-label="Remove course"
        >
          <Trash2 size={20} />
        </Button>
      </div>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        title="Remove course?"
        description="Are you sure you want to remove this course from your courses list? You can add it back later from the Add course modal."
        confirmText="Yes"
        cancelText="No"
        isLoading={isRemoving}
        onClose={onCloseDeleteModal}
        onConfirm={onConfirmDelete}
      />
    </Can>
  );
};
